
import {SeverityLevel, init, ErrorEvent, EventHint, captureMessage, captureException} from '@sentry/browser'
import { UrlParams } from './urlparams';

export const SENTRY_DSN = 'https://da8e408f13b70b78e09419156fab9ab7@o4507056858988544.ingest.us.sentry.io/4507057035149312';

export enum LogLevelEnum {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    CRITICAL = 4,
}

export enum TelemetryExitCode {
    SUCCESS = 0,
    FATAL = 1,
    REJECTED = 2,
}

export class Telemetry
{

    private logLevel: LogLevelEnum;
    private credentialString : string;

    constructor(logLevel: LogLevelEnum = LogLevelEnum.WARN, credentialString: string = '') {
        this.logLevel = logLevel;
        this.credentialString = credentialString;
        init(
            {
                dsn: credentialString,
                integrations: [],
                beforeSend(event: ErrorEvent, hint: EventHint) {
                    const message = event.exception.values[0].value || '';
                    if (message.indexOf('JEST') !== -1) {
                        return null;
                    }
                    else if (message.indexOf('Fountain::Telemetry') !== -1) {
                        return event;
                    }
                    return null;
                },
                // Performance Monitoring
                // tracesSampleRate: 1.0,
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
                // Session Replay
                // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            }
        );
    }

    public checkForTest() : void 
    {
        const kswitch = UrlParams.getOneOf(['fntn-tel-test'], UrlParams.parse());
        if (kswitch.length) {
            this.log(LogLevelEnum.CRITICAL, 'Telemetry test', {});
        }
    }

    public async log(
        level: LogLevelEnum,
        message: string, 
        context: Record<string, string | number | null>
    ) : Promise<TelemetryExitCode>
    {
        if (!this.credentialString) {
            return TelemetryExitCode.REJECTED;
        }
        try {
            return await this.tryLog(level, message, context);
        } catch (error) {
            console.error(error);
            return TelemetryExitCode.FATAL;
        }
    }

    public async tryLog(
        level: LogLevelEnum,
        message: string, 
        context: Record<string, string | number | null>
    ) : Promise<TelemetryExitCode>
    {
        message = `Fountain::Telemetry: ${message}`;
        if (level >= this.logLevel) {
            const timestamp = (new Date()).getMilliseconds();
            const body: Record<string, string | number | null | Record<string, string | number | null>> = {
                timestamp: timestamp,
                message: message,
                logtype: 'javascript',
                service: 'fountain-analytics',
                hostname: window.location.hostname,
                attributes: context,
            }
            let severityLevel : SeverityLevel = null;
            switch (level) {
                case LogLevelEnum.INFO:
                    severityLevel = 'info';
                    break;
                case LogLevelEnum.WARN:
                    severityLevel = 'warning';
                    break;
                case LogLevelEnum.ERROR:
                    severityLevel = 'error';
                    break;
                case LogLevelEnum.CRITICAL:
                    severityLevel = 'fatal';
                    break;
                default:
                    severityLevel = 'debug';
            }
            let messageId = null;
            if (level >= LogLevelEnum.ERROR) {
                messageId = captureException(new Error(JSON.stringify(body)));
            } else {
                messageId = captureMessage(JSON.stringify(body), severityLevel);
            }
            if (!messageId) {
                return TelemetryExitCode.REJECTED;
            }
            return TelemetryExitCode.SUCCESS;
        } else {
            console.debug(message);
            return TelemetryExitCode.REJECTED;
        }
    }

}